import { apiFetch } from "../api/client";

/**
 * LA BILLETTERIE — les articles d'un événement, et ce qu'ils coûtent.
 *
 *   GET /v2/domains/:d/events/:e/articles                  api-v1   le catalogue
 *   GET /v2/domains/:d/events/:e/articles/users/:u         api-v1   ce qu'il a retenu
 *   PUT /v2/domains/:d/events/:e/articles/users/:u         api-v1   ce qu'il retient
 *   GET /domains/:d/events/:e/users/:u/invoices/resume     apix     ce qu'il doit
 *
 * ⚠️ CE N'EST PAS UN PANIER, ET LE PROTOTYPE EN MONTRE UN. Sa billetterie a des
 * quantités (« − 2 + »), un prix par jour de présence et un prix par accompagnant : rien
 * de tout cela n'existe dans le produit. Un article s'y COCHE, jour par jour — la table
 * `ArticleUsers` porte une ligne par (article, jour, personne) et ne connaît pas de
 * colonne « quantité ». Livrer les molettes du prototype aurait donné des chiffres que
 * la facture aurait ensuite démentis.
 *
 * ⚠️ ET LE TOTAL NE SE CALCULE PAS ICI. Le prototype l'additionne dans le navigateur ;
 * le produit le demande au serveur, parce que le prix dépend de choses que cet écran ne
 * peut pas connaître — quatre déductions, lues dans `api/src/routes/invoices/resume` :
 *
 *   ce qui est RETENU    une ligne par (article, jour, personne), et pas seulement les
 *                        siennes : la jointure prend aussi ceux qu'on a inscrits
 *                        (`UserEvents.registeredBy2`)
 *   les OFFERTS          table `FreeArticles`, plafonnés à la quantité réellement prise
 *   la REMISE            un code (`ArticleCodes`) portant un pourcentage sur UN article,
 *                        dont le contingent se partage — le serveur compte ce que les
 *                        autres en ont déjà consommé pour savoir ce qui reste
 *   le DÉJÀ PAYÉ         la somme des `InvoicesV2` réglées
 *
 * plus la TVA, qui se règle PAR ARTICLE (l'événement de test en porte à 0 %, 10 % et
 * 20 %). Un total recalculé ici serait faux le jour où l'un de ces leviers sert.
 *
 * ⚠️ DEUX ROUTES `resume` COEXISTENT, ET CE N'EST PAS CELLE-CI QUI PORTE TOUT.
 * `api-v1/routesV2/invoicesV2/getResume` en connaît deux autres — un gratuit tous les N
 * (`Articles.oneFreeWith`) et une remise par société (`FO_PAY_COMPANY_REMISE`) — que
 * celle d'apix, la seule que ce front appelle, N'APPLIQUE PAS. Les attribuer à la
 * mauvaise ferait chercher un rabais là où rien ne l'accorde ; c'est la route d'apix qui
 * fait foi ici, parce que c'est elle qui chiffre ce qu'on affiche.
 */

/** Ce que la base porte pour un article, une fois lu. */
export type Article = {
  id: number;
  /** Le nom vendu au participant. */
  label: string;
  /** Le prix HORS TAXES — c'est celui que la V1 affiche, et celui que la base nomme `price`. */
  priceExclTax: number;
  /** Toutes taxes ; la colonne existe, remplie par le back office. */
  priceInclTax: number;
  /** Le taux, en pourcentage. Par article, pas par événement. */
  vat: number;
  /**
   * PAR JOUR — la clé est un `EventDays.id`, et **`0` désigne ce qui se vend hors
   * jour** (le pass de l'événement entier). C'est la convention de la V1, qui compose
   * sa liste avec `[{ id: 0 }, ...jours retenus]`.
   */
  days: Record<string, ArticleDay>;
  /** L'ordre voulu au back office. Absent sur la plupart des lignes. */
  order: number | null;
};

/** Ce qu'un article devient un jour donné : proposé, verrouillé, ou seul choix possible. */
export type ArticleDay = {
  /** Proposé ce jour-là. Sans lui, l'article n'existe pas pour ce jour. */
  visible: boolean;
  /** Montré mais non choisissable — le back office l'a fermé. */
  closed: boolean;
  /** Ce jour-là, on ne retient qu'UN article : voir `choixUnique`. */
  unique: boolean;
};

/**
 * CE QUE LE PARTICIPANT RETIENT — la forme même qu'attend la route d'écriture :
 * `{ "0": [12], "1643": [8, 9] }`, une liste d'identifiants d'articles par jour.
 */
export type Selection = Record<string, number[]>;

/** Un groupe de la page : un jour, et ce qui s'y vend. */
export type SalesDay = {
  /** `0` pour les articles vendus hors jour. */
  id: number;
  /** Le jour tel qu'il s'écrit. **Vide pour le niveau hors-jour**, comme dans la V1. */
  label: string;
  /**
   * UN SEUL ARTICLE RETENU CE JOUR-LÀ.
   *
   * ⚠️ IL SUFFIT D'UN SEUL ARTICLE MARQUÉ POUR QUE TOUT LE JOUR LE DEVIENNE — la règle
   * de la V1 (`articles.find(f => f.days?.[id]?.isRadio)`), et elle surprend : marquer
   * un unique tarif « exclusif » transforme la liste entière en choix unique, y compris
   * les articles qui ne le sont pas.
   */
  singleChoice: boolean;
  articles: Article[];
};

type RawDay = { isShow?: unknown; isDisabled?: unknown; isRadio?: unknown };

type RawArticle = {
  id?: number;
  label?: string | null;
  labelEN?: string | null;
  price?: unknown;
  priceTTC?: unknown;
  tva?: unknown;
  days?: Record<string, RawDay> | null;
  order?: unknown;
};

const yes = (v: unknown) => v === true || v === 1;
const count = (v: unknown) =>
  typeof v === "number" && Number.isFinite(v) ? v : 0;

/**
 * ⚠️ LE LIBELLÉ ANGLAIS EXISTE ET LA V1 NE LE LIT NULLE PART. `labelEN` se saisit au
 * back office (`bo/src/pages/articles/Edition.jsx`), s'importe même en masse — et
 * `fo-meeting` ne le mentionne pas une seule fois : un anglophone paie un « Pass Early
 * Bird Adhérent » en français. On le sert donc, sans en faire une cascade complète : la
 * base n'a que ces deux colonnes, et prétendre traduire en italien parce que
 * l'événement propose l'italien serait promettre ce qu'aucune colonne ne porte.
 */
function articleOf(raw: RawArticle, english: boolean): Article | null {
  if (typeof raw?.id !== "number") return null;

  const englishLabel = (raw.labelEN ?? "").trim();
  const frenchLabel = (raw.label ?? "").trim();
  const label =
    (english && englishLabel ? englishLabel : frenchLabel) ||
    // Un article sans nom reste choisissable plutôt que d'ouvrir un blanc au milieu
    // d'une liste de prix : c'est la règle déjà tenue par les conférences.
    `Article ${raw.id}`;

  const days: Record<string, ArticleDay> = {};
  for (const [day, rawDay] of Object.entries(raw.days ?? {})) {
    days[day] = {
      visible: yes(rawDay?.isShow),
      closed: yes(rawDay?.isDisabled),
      unique: yes(rawDay?.isRadio),
    };
  }

  return {
    id: raw.id,
    label,
    priceExclTax: count(raw.price),
    priceInclTax: count(raw.priceTTC),
    vat: count(raw.tva),
    days,
    order: typeof raw.order === "number" ? raw.order : null,
  };
}

/**
 * LE CATALOGUE DE L'ÉVÉNEMENT.
 *
 * ⚠️ RANGÉ ICI, PARCE QUE LA ROUTE NE LE RANGE PAS. Son `findAll` n'a pas de `ORDER BY`,
 * donc l'ordre rendu est celui que la base veut bien donner ; la colonne `order`, elle,
 * existe et le back office la remplit. On suit l'intention déclarée quand elle est là,
 * et l'identifiant sinon — ce qui, sur un événement où personne n'a rangé les articles,
 * redonne exactement l'ordre de création.
 */
export async function fetchArticles(
  domainId: number,
  eventId: number,
  english: boolean,
  signal?: AbortSignal,
): Promise<Article[]> {
  const answer = await apiFetch<RawArticle[] | null>(
    `/v2/domains/${domainId}/events/${eventId}/articles`,
    { signal },
  );
  return (answer ?? [])
    .flatMap((a) => {
      const article = articleOf(a, english);
      return article ? [article] : [];
    })
    .sort(
      (a, b) =>
        (a.order ?? Number.MAX_SAFE_INTEGER) -
          (b.order ?? Number.MAX_SAFE_INTEGER) || a.id - b.id,
    );
}

/**
 * CE QUE LE SERVEUR RETIENT DÉJÀ POUR CETTE PERSONNE.
 *
 * ⚠️ `isSelected=true` COMME LA V1, et ce n'est pas un filtre de confort : sans lui la
 * route rend aussi ce qui n'est pas retenu, et l'écran rouvrirait coché ce que le
 * participant avait laissé de côté.
 */
export async function fetchSelection(
  domainId: number,
  eventId: number,
  userId: number,
  signal?: AbortSignal,
): Promise<Selection> {
  const answer = await apiFetch<{ days?: Record<string, unknown> } | null>(
    `/v2/domains/${domainId}/events/${eventId}/articles/users/${userId}?isSelected=true`,
    { signal },
  );
  return readSelection(answer?.days);
}

/**
 * CE QUI REVIENT DE LA ROUTE, RAMENÉ À DES NOMBRES.
 *
 * Exporté pour son test : la route rend une colonne JSON, et rien ne garantit que ses
 * listes ne contiennent que des identifiants — une valeur de travers renverrait vers un
 * article inexistant, ce qui fait tomber le calcul du total (voir `fetchResume`).
 */
export function readSelection(raw: unknown): Selection {
  if (!raw || typeof raw !== "object") return {};
  const selection: Selection = {};
  for (const [day, list] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(list)) continue;
    selection[day] = list.filter(
      (id): id is number => typeof id === "number" && Number.isFinite(id),
    );
  }
  return selection;
}

/**
 * CE QU'ON PROPOSE, JOUR PAR JOUR.
 *
 * ⚠️ LE JOUR `0` D'ABORD, PUIS LES JOURS RETENUS — dans cet ordre, et **seulement les
 * jours que le participant a cochés à l'étape précédente** : la V1 compose
 * `[{ id: 0 }, ...event.days.filter(retenus)]`. Proposer le déjeuner d'un jour où l'on
 * ne vient pas ferait payer un repas qu'on ne prendra pas — et la route d'écriture
 * l'ignorerait de toute façon (`if (!ued && dayId !== 0) return null`), donc l'écran
 * aurait encaissé une intention que le serveur jette.
 *
 * ⚠️ ET LES JOURS SANS RIEN À VENDRE DISPARAISSENT, toujours comme la V1
 * (`.filter(f => f.articles.length > 0)`) : un en-tête de journée suivi du vide se lit
 * comme une panne.
 */
export function salesDays(
  articles: ReadonlyArray<Article>,
  days: ReadonlyArray<{ id: number; label: string }>,
  /**
   * Ce que le participant a déjà — sélection en cours, pré-sélection du back office,
   * commande enregistrée. Omise, l'étalage se limite à ce qui est en vente.
   */
  selected: Selection = {},
): SalesDay[] {
  return [{ id: 0, label: "" }, ...days]
    .map((day) => {
      const key = String(day.id);
      /**
       * ═══ ON MONTRE CE QUI EST EN VENTE, ET AUSSI CE QU'ON A DÉJÀ ═══
       *
       * ⚠️ « VISIBLE » NE COMMANDE PLUS SEUL L'AFFICHAGE. Le back office a trois
       * interrupteurs par jour et par article : Visible (`isShow`), Pré-sélectionné
       * (`isSelected`), Non modifiable (`isDisabled`). Un article PRÉ-SÉLECTIONNÉ mais
       * non visible entrait bien dans la commande du participant sans apparaître nulle
       * part : il payait une ligne qu'il ne pouvait pas lire. Vu sur SPORT ACHAT ÉTÉ
       * 2026, dont l'unique article — la participation à 90 € — est pré-sélectionné hors
       * jour et visible nulle part : l'écran annonçait « rien à proposer ici ».
       *
       * ⚠️ CE QU'ON A DÉJÀ VIENT AUSSI DE LA COMMANDE ENREGISTRÉE, et pas seulement de
       * la pré-sélection : un article acheté puis MASQUÉ au back office doit rester
       * lisible, sinon il disparaît de l'écran en restant sur la facture.
       *
       * ⚠️ MAIS ON N'INVENTE PAS UNE OFFRE. Un article ni visible ni retenu ne s'affiche
       * pas : sur cet événement, il serait apparu trois fois — une par journée déclarée
       * — en lignes inertes que personne ne peut cocher et qui ne coûtent rien.
       */
      const alreadyHas = new Set(selected[key] ?? []);
      const offered = articles.filter(
        (a) => a.days[key]?.visible || alreadyHas.has(a.id),
      );
      return {
        id: day.id,
        label: day.label,
        singleChoice: offered.some((a) => a.days[key]?.unique),
        articles: offered,
      };
    })
    .filter((day) => day.articles.length > 0);
}

/**
 * ═══ UN ARTICLE POUR CHAQUE JOURNÉE PROPOSÉE ? ═══ la question de « Un choix
 * obligatoire par journée » (`FO_MEETING_V2_ARTICLES_REQUIRED`), posée ici pour que le
 * bouton de l'étape et le fil d'Ariane posent LA MÊME.
 *
 * ⚠️ ELLE NE VIVAIT QUE SUR LE BOUTON, comme la règle des dates avant le bug #257. Le fil
 * ouvrait les étapes suivantes sans la consulter : prendre un billet, « Continuer »,
 * revenir à la billetterie par le fil, tout décocher, repartir vers la fin par le fil, et
 * la confirmation enregistrait une inscription sans billet. Mesuré sur la pile locale le
 * 2026-10-27.
 *
 * ⚠️ PAR JOURNÉE PROPOSÉE, celle du niveau « hors jour » comprise : la règle de la V1, une
 * par `Form.Item`. Une journée sans rien à vendre n'est pas proposée (`salesDays` l'a déjà
 * retirée), donc un événement qui ne vend rien ne bloque personne.
 */
export function eachDayChosen(
  stalls: ReadonlyArray<SalesDay>,
  selection: Selection,
): boolean {
  return stalls.every((day) => (selection[String(day.id)] ?? []).length > 0);
}

/**
 * CET ARTICLE SE MODIFIE-T-IL, CE JOUR-LÀ ?
 *
 * ⚠️ DEUX RAISONS DE LE VERROUILLER, ET UNE SEULE ÉTAIT LUE. « Non modifiable » est un
 * interrupteur du back office ; mais un article qu'on affiche PARCE QU'ON L'A, sans
 * qu'il soit en vente, ne se décoche pas non plus — il n'a jamais été proposé, le
 * reprendre reviendrait à vendre ce que l'organisateur n'a pas mis en vente.
 */
export function locked(article: Article, dayId: number): boolean {
  const day = article.days[String(dayId)];
  return day?.closed === true || day?.visible !== true;
}

/** Cet article est-il retenu ce jour-là ? */
export function isSelected(
  selection: Selection,
  dayId: number,
  articleId: number,
): boolean {
  return (selection[String(dayId)] ?? []).includes(articleId);
}

/**
 * RETIENT L'ARTICLE, OU LE REND.
 *
 * ⚠️ EN CHOIX UNIQUE, RECLIQUER SUR CELUI QU'ON A PRIS LE RETIRE — et c'est le seul
 * écart assumé avec la V1, dont le `Radio.Group` ne sait pas revenir à « rien ». Son
 * étape n'est pourtant obligatoire que si `FO_MEETING_ARTICLES_REQUIRED` le dit : sur
 * les autres, un clic malheureux sur un pass à 900 € s'y grave définitivement, et il ne
 * reste qu'à écrire à l'organisateur. Le bouton porte donc `aria-pressed` — c'est un
 * interrupteur, pas un bouton radio, et il s'annonce comme tel.
 */
export function toggle(
  selection: Selection,
  day: SalesDay,
  articleId: number,
): Selection {
  const key = String(day.id);
  const before = selection[key] ?? [];
  const retire = before.includes(articleId);

  const after = retire
    ? before.filter((id) => id !== articleId)
    : day.singleChoice
      ? [articleId]
      : [...before, articleId];

  return { ...selection, [key]: after };
}

/**
 * LES IDENTIFIANTS À PLAT, DOUBLONS COMPRIS.
 *
 * ⚠️ LES DOUBLONS SONT LA QUANTITÉ. Le même article retenu sur deux jours doit partir
 * DEUX FOIS : le serveur compte les occurrences (`groupByArticleId`) pour en faire une
 * quantité, et le dédoublonner ici facturerait un seul déjeuner à qui en a pris deux.
 *
 * ⚠️ FILTRÉS CONTRE LE CATALOGUE, ET C'EST UNE PROTECTION, PAS UNE POLITESSE. La route
 * du total fait `articles.find(...)` sur chaque identifiant reçu et empile le résultat
 * SANS le vérifier : un identifiant qu'elle ne retrouve pas devient `undefined`, et la
 * ligne suivante lit `undefined.priceTTC`. Un seul intrus — une sélection périmée, un
 * article supprimé au back office entre-temps — et la réponse est un 500 au lieu d'un
 * total.
 */
export function flatIds(
  selection: Selection,
  known: ReadonlyArray<Article>,
): number[] {
  const catalogue = new Set(known.map((a) => a.id));
  return Object.values(selection)
    .flat()
    .filter((id) => catalogue.has(id));
}

/**
 * NETTOIE LA SÉLECTION AVANT DE L'ÉCRIRE.
 *
 * ⚠️ MÊME PROTECTION QU'`idsAplat`, DU CÔTÉ DE L'ÉCRITURE. `enregistreSelection` DÉTRUIT
 * la commande avant de la recréer : une sélection qui la fait échouer laisse la personne
 * sans rien. Deux filtres, tous deux facultatifs pour rester utilisables quand la donnée
 * n'est pas connue :
 *
 *   par JOUR      un article coché sur un jour ensuite décoché reste dans la sélection,
 *                 invisible à l'écran (l'étalage le masque) mais chiffré, écrit et
 *                 facturé. Le jour `0` (hors-jour) ne dépend d'aucune présence : gardé.
 *   par CATALOGUE un identifiant périmé (article supprimé au back office, état de
 *                 navigation forgé) fait `articles.find(...) === undefined` côté serveur,
 *                 la ligne suivante lit `undefined.priceTTC`, et la commande détruite
 *                 n'est jamais recréée.
 */
export function cleanSelection(
  selection: Selection,
  {
    selectedDays,
    known,
  }: {
    /** Les `EventDays.id` retenus. Absent : pas de filtre par jour (pas d'étape dates). */
    selectedDays?: ReadonlyArray<number>;
    /** Le catalogue. Absent : pas de filtre par catalogue. */
    known?: ReadonlyArray<Article>;
  } = {},
): Selection {
  const days = selectedDays ? new Set(selectedDays.map(String)) : null;
  const catalogue = known ? new Set(known.map((a) => a.id)) : null;

  const own: Selection = {};
  for (const [day, ids] of Object.entries(selection)) {
    // Le hors-jour (`0`) ne dépend d'aucune présence : jamais filtré par jour.
    if (days && day !== "0" && !days.has(day)) continue;
    const kept = catalogue ? ids.filter((id) => catalogue.has(id)) : ids;
    if (kept.length > 0) own[day] = kept;
  }
  return own;
}

/**
 * CE QUE LE FILTRE PAR JOUR DE `cleanSelection` RETIRE : les billets posés sur un jour
 * qui n'est plus retenu. Le hors-jour (`0`) n'y est jamais, pour la même raison.
 *
 * ⚠️ CE SONT LES BILLETS QUI TOMBENT SANS QU'ON LES VOIE. L'étalage ne montre que les
 * jours retenus : un billet du mardi décoché disparaît de l'écran avant de disparaître
 * de la commande, réglé ou non. Voir `funnel/stepSave.tsx`, qui prévient avant.
 */
export function droppedByDays(
  selection: Selection,
  selectedDays: ReadonlyArray<number>,
): Selection {
  const days = new Set(selectedDays.map(String));
  const dropped: Selection = {};
  for (const [day, ids] of Object.entries(selection)) {
    if (day !== "0" && !days.has(day) && ids.length > 0) dropped[day] = ids;
  }
  return dropped;
}

/**
 * ═══ DEUX COMMANDES, LA MÊME ? ═══ pour ne pas réécrire ce qui ne change pas.
 *
 * ⚠️ CE N'EST PAS UNE COQUETTERIE DE VITESSE, MÊME SI ELLE EN FAIT GAGNER.
 * La route qu'appelle `saveSelection` DÉTRUIT la commande avant de la recréer : la réécrire à
 * l'identique détruit puis recrée des lignes DÉJÀ RÉGLÉES, pour rien — et la
 * confirmation, qui attend cet aller-retour derrière son « Enregistrement… », le fait
 * attendre pour rien aussi. C'est la règle qu'`orderToRewrite` tient déjà avant
 * d'appeler l'écriture (voir `funnel/stepSave.tsx`) ; celle-ci la tient DANS l'écriture,
 * là où le parcours complet passe.
 *
 * ⚠️ INCONNUE VEUT DIRE « ON ÉCRIT ». `undefined`, ce n'est pas une commande vide : c'est
 * qu'on n'a pas lu ce qui est enregistré. Répondre « identique » sur une lecture qu'on
 * n'a pas eue laisserait une correction sur le carreau.
 *
 * Un jour sans article ne compte pas — `{}` et `{ "41": [] }` désignent la même commande
 * vide — et l'ordre des identifiants n'en fait pas deux non plus.
 */
export function sameSelection(
  selection: Selection,
  recorded: Selection | undefined,
): boolean {
  if (!recorded) return false;
  const shape = (s: Selection) =>
    Object.entries(s)
      .flatMap(([day, ids]) =>
        ids.length > 0
          ? [`${day}:${[...ids].sort((a, b) => a - b).join(",")}`]
          : [],
      )
      .sort()
      .join("|");
  return shape(selection) === shape(recorded);
}

/** Ce que la sélection pèse, une fois écrite. */
export function toBody(selection: Selection): { days: Selection } {
  return { days: selection };
}

/**
 * LE TOTAL, TEL QUE LE SERVEUR LE CALCULE.
 *
 * Les montants sont en euros. `HT` et `TTC` cohabitent parce que le produit facture les
 * deux : le type de facturation décide lequel fait foi (`withTva`).
 */
export type Resume = {
  /** Le sous-total des articles retenus, avant toute déduction. */
  subtotalExclTax: number;
  /** Ce qui est offert — `oneFreeWith`, et les articles gratuits attribués. */
  freeExclTax: number;
  /** Ce qu'un code de remise enlève. */
  discountExclTax: number;
  /**
   * Ce qui a DÉJÀ été facturé et payé.
   *
   * ⚠️ LE TTC EST CELUI QUI S'AFFICHE : c'est la somme que la personne a sortie. Le
   * hors-taxes reste là pour le décompte des articles, qui se tient en HT jusqu'à la
   * ligne de TVA.
   */
  alreadyPaidExclTax: number;
  alreadyPaidInclTax: number;
  /** Ce qui reste dû. */
  remainingExclTax: number;
  remainingInclTax: number;
  /**
   * LA COMMANDE ENTIÈRE, TOUTES TAXES : après les déductions et la TVA, AVANT ce qui a
   * déjà été payé. C'est la ligne qui relie le hors-taxes au « Déjà réglé », qui se dit
   * en TTC : sans elle, le lecteur soustrayait l'un de l'autre et ne retombait pas sur
   * le reste (bug #201).
   */
  orderInclTax: number;
  /** Le détail de la TVA, taux par taux. */
  vatLines: ReadonlyArray<{ rate: number; amount: number }>;
  /**
   * LES BILLETS COMPTÉS, ACCOMPAGNANTS COMPRIS.
   *
   * ⚠️ LE SERVEUR FACTURE LE GROUPE ENTIER : la commande d'un accompagnant revient au
   * porteur (`UE.registeredBy2`, dans `api/src/routes/invoices/resume`). Le total les
   * compte donc, et une liste tirée de la seule sélection du porteur en taisait une
   * partie. Regroupés par article, d'où la quantité.
   */
  lines: ReadonlyArray<{ id: number; label: string; quantity: number }>;
  /** Il reste quelque chose à payer — le seuil du serveur est 0,50 €. */
  toPay: boolean;
  /**
   * LE CODE PROMO POSÉ SUR LA COMMANDE, s'il y en a un (`ArticleCodeUsers`).
   *
   * ⚠️ IL PEUT N'ENLEVER RIEN : un code vise UN article, et la remise reste à zéro tant
   * que la commande ne le contient pas. C'est pourquoi on le rend même alors — sans
   * son nom et son article, la personne qui vient de le saisir verrait un total
   * inchangé sans rien pour comprendre. Voir `funnel/PromoCode.tsx`.
   */
  promoCode: PromoCode | null;
};

export type PromoCode = {
  code: string;
  /** Le pourcentage, plafonné à 100 comme le serveur le plafonne (bug #361). */
  rate: number;
  /** Le libellé de l'article visé, tel que le serveur le donne. */
  article: string;
  /**
   * ⚠️ IL A SERVI À UN PAIEMENT (`ArticleCodeUsers.isUsed`, posé par la facture payée) :
   * il ne se change plus depuis le site, et le serveur le refuse aussi (`CODE_IS_USED`).
   * Lu sur `code.usedByUser`, PAS sur le `isUsed` de tête de la réponse : celui-là est
   * toujours vide (voir `api/src/routes/invoices/resume`).
   */
  locked: boolean;
};

type RawResume = {
  currHT?: unknown;
  freeHT?: unknown;
  /* Le nom du serveur, en français : c'est un contrat, pas un choix de style. */
  remiseHT?: unknown;
  prevHT?: unknown;
  prevTTC?: unknown;
  totalHT?: unknown;
  totalTTC?: unknown;
  tvas?: ReadonlyArray<{ tva?: unknown; totalTVA?: unknown }> | null;
  canPay?: unknown;
  articles?: ReadonlyArray<{
    id?: unknown;
    label?: string | null;
    /* Le « Label facture » du back office : celui que la facture imprime. */
    label2?: string | null;
    quantity?: unknown;
  }> | null;
  /* La commande entière, TTC. Une API d'avant ne la rend que dans la facture préparée. */
  orderTTC?: unknown;
  /* ⚠️ Depuis le bug #543, la facture d'un second achat ne porte que le nouveau : son
     `totalTTC` n'est plus celui de la commande dès qu'une facture est déjà réglée. */
  invoice?: { bill?: { resume?: { totalTTC?: unknown } | null } | null } | null;
  /* La ligne `ArticleCodes` du code posé, et le nom de son article. */
  /* `usedByUser` : `ArticleCodeUsers.isUsed`, 0/1 en base, absent sur un serveur d'avant. */
  code?: { code?: unknown; remise?: unknown; article?: unknown; usedByUser?: unknown } | null;
};

/**
 * DEMANDE LE TOTAL, POUR UNE SÉLECTION QUI N'EST PAS ENCORE ÉCRITE.
 *
 * ⚠️ LES IDENTIFIANTS PARTENT SÉPARÉS PAR DES VIRGULES, dans UN paramètre. Le serveur
 * fait `articles.split(',')` : c'est une chaîne qu'il attend, pas une clé répétée. La V1
 * envoie bien `articles=1&articles=2` — ça ne marche que parce que la passerelle HTTP
 * d'AWS recolle les valeurs répétées avec une virgule avant de les lui donner. Dépendre
 * de ce recollage, c'est écrire du code qui marche en production et pas ailleurs.
 *
 * ⚠️ DEUX QUESTIONS DIFFÉRENTES, ET C'EST L'APPELANT QUI TRANCHE. `null` demande « ce que
 * doit la commande ENREGISTRÉE » : le paramètre est absent, et c'est ce que veulent la
 * confirmation, le paiement et le badge. Une LISTE, même vide, demande « ce que coûterait
 * cette sélection-ci » : le paramètre part, vide s'il le faut, et c'est ce que veut
 * l'étape billetterie quand on vient de tout décocher.
 *
 * ⚠️ LES DEUX SE CONFONDAIENT, ET ÇA SE VOYAIT À L'ÉCRAN. Une liste vide n'envoyait rien,
 * donc le total affiché sous un panier vidé redevenait celui de la commande enregistrée
 * (signalé le 2026-08-05 : « Reste à régler 750 € » sur un panier vide). Le serveur ne
 * savait pas non plus l'exprimer — `"".split(',')` rend `[""]`, un article introuvable qui
 * le faisait répondre 500 ; c'est corrigé de son côté, une commande vide se demande
 * maintenant.
 */
export async function fetchResume(
  domainId: number,
  eventId: number,
  userId: number,
  /** `null` : la commande enregistrée. Une liste : cette sélection-ci, vide comprise. */
  ids: ReadonlyArray<number> | null,
  { signal }: { signal?: AbortSignal } = {},
): Promise<Resume> {
  const params = new URLSearchParams();
  if (ids !== null) params.set("articles", ids.join(","));
  // ⚠️ TOUJOURS AVEC TVA : tout se facture TTC (#544), et le serveur compare au seuil de
  // 0,50 € le total que ce drapeau désigne. Il compare la CHAÎNE `'true'` : l'absence du
  // paramètre vaudrait hors taxes.
  params.set("withTva", "true");

  const raw = await apiFetch<RawResume | null>(
    `/domains/${domainId}/events/${eventId}/users/${userId}/invoices/resume?${params}`,
    { api: "apix", signal },
  );
  return readResume(raw);
}

/** Exporté pour son test : les montants d'une facture ne se devinent pas. */
export function readResume(raw: unknown): Resume {
  const r = (raw ?? {}) as RawResume;
  // Repli sur le hors-taxes : une API plus ancienne ne rend pas encore `prevTTC`, et
  // une ligne « Déjà réglé » qui disparaît serait pire qu'une ligne hors taxes.
  const alreadyPaidInclTax =
    r.prevTTC === undefined ? count(r.prevHT) : count(r.prevTTC);
  return {
    subtotalExclTax: count(r.currHT),
    freeExclTax: count(r.freeHT),
    discountExclTax: count(r.remiseHT),
    alreadyPaidExclTax: count(r.prevHT),
    alreadyPaidInclTax,
    remainingExclTax: count(r.totalHT),
    remainingInclTax: count(r.totalTTC),
    /* Repli sur l'identité du serveur (reste = commande − déjà payé) : une réponse sans
       facture préparée ne doit pas afficher une commande à zéro. */
    orderInclTax:
      typeof r.orderTTC === "number"
        ? count(r.orderTTC)
        : typeof r.invoice?.bill?.resume?.totalTTC === "number"
          ? count(r.invoice.bill.resume.totalTTC)
          : Math.round((count(r.totalTTC) + alreadyPaidInclTax) * 100) / 100,
    /* ⚠️ SEULS LES BILLETS : le serveur range dans la même liste les gratuits et les
       remises, en quantité négative ou nulle. Ils ont déjà leurs lignes à eux
       (« Offert », « Remise ») ; les recompter ici ferait des billets fantômes. */
    lines: (r.articles ?? []).flatMap((a) => {
      const quantity = count(a?.quantity);
      if (typeof a?.id !== "number" || quantity <= 0) return [];
      // Le nom vendu d'abord, comme le catalogue : `label2` est le « Label facture »
      // du back office, un libellé de comptabilité et non celui qu'on a choisi.
      const label =
        (a.label ?? "").trim() || (a.label2 ?? "").trim() || `Article ${a.id}`;
      return [{ id: a.id, label, quantity }];
    }),
    vatLines: (r.tvas ?? []).flatMap((t) =>
      count(t?.totalTVA) === 0
        ? []
        : [{ rate: count(t?.tva), amount: count(t.totalTVA) }],
    ),
    toPay: r.canPay === true,
    promoCode:
      typeof r.code?.code === "string" && r.code.code !== ""
        ? {
            code: r.code.code,
            rate: Math.min(Math.max(count(r.code.remise), 0), 100),
            article: typeof r.code.article === "string" ? r.code.article : "",
            locked: yes(r.code.usedByUser),
          }
        : null,
  };
}

/**
 * POSE UN CODE PROMO SUR LA COMMANDE DE CETTE PERSONNE (`api/src/routes/articleCodes/setCodeUser`).
 *
 * ⚠️ RIEN N'EST RENDU : le serveur écrit `ArticleCodeUsers`, et c'est le solde
 * (`articles-resume`) qui dit ensuite ce que le code enlève. L'appelant doit donc le
 * périmer — voir la liste des écrivains dans `funnel/useArticles.ts`.
 *
 * Ses refus portent un mot du produit en message : `CODE_NOT_FOUND`,
 * `CODE_OUT_OF_STOCK`, `CODE_IS_USED` (un code qui a déjà servi à un paiement).
 */
export async function applyPromoCode(
  domainId: number,
  eventId: number,
  userId: number,
  code: string,
): Promise<void> {
  await apiFetch(
    `/domains/${domainId}/events/${eventId}/users/${userId}/codes`,
    { api: "apix", method: "PUT", body: { code: code.trim() } },
  );
}

/**
 * ÉCRIT LA SÉLECTION.
 *
 * ⚠️ ELLE REMPLACE TOUT. La route commence par un `destroy` de ce que la personne avait
 * retenu sur cet événement, puis récrée d'après ce qu'on envoie : un corps partiel n'est
 * pas une mise à jour partielle, c'est une suppression du reste. C'est pourquoi
 * l'appelant n'écrit QUE s'il a une sélection à écrire — voir `funnel/participant.ts`.
 */
export async function saveSelection(
  domainId: number,
  eventId: number,
  userId: number,
  selection: Selection,
): Promise<void> {
  await apiFetch(
    `/v2/domains/${domainId}/events/${eventId}/articles/users/${userId}`,
    { method: "PUT", body: toBody(selection) },
  );
}

/**
 * UN MONTANT, ÉCRIT.
 *
 * ⚠️ PAR `Intl`, DONC DANS LA LANGUE DU LECTEUR — « 900,00 € » en français, « €900.00 »
 * en anglais. La V1 écrit `price.toFixed(2) + ' € H.T.'`, ce qui donne « 900.00 € » à un
 * francophone : le point décimal d'un prix est une faute qui se voit, et sur un écran
 * qui engage de l'argent elle se paie en confiance.
 */
export function euros(amount: number, locale: string): string {
  const options: Intl.NumberFormatOptions = {
    style: "currency",
    currency: "EUR",
  };
  try {
    return new Intl.NumberFormat(locale, options).format(amount);
  } catch {
    return new Intl.NumberFormat(undefined, options).format(amount);
  }
}
